import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Clock, Truck, Shield, Zap, Users, DollarSign,
  Wrench, Star, Menu, X, ChevronLeft, ChevronRight, MessageCircle,
  Calculator, Calendar, Car, Facebook, Instagram, ArrowRight,
} from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../context/SettingsContext';

const BL = '#0D2B6B', YW = '#F5C400';

const SLIDES = [
  {
    img: '/images/truck2.jpg',
    headline: "Brisbane's Most Trusted Towing Service",
    sub: 'Professional, fast & reliable towing across all Brisbane suburbs — North, South, East & West. 24/7, always.',
    cta: 'Take Quote Now', to: '/book',
    cta2: 'Get Free Estimate', to2: '/estimate',
  },
  {
    img: '/images/truck3.jpg',
    headline: 'Emergency Towing 24/7',
    sub: "Stuck anywhere in Brisbane or SE Queensland? We'll be with you in 30 minutes or less — any suburb, any time.",
    cta: 'Call Now', to: null,
    cta2: 'Book Online', to2: '/book',
  },
  {
    img: '/images/truck1.jpg',
    headline: 'Free Car Removal & Cash for Cars',
    sub: 'We come to you — completely free. Turn your unwanted car into instant cash today. All Brisbane areas covered.',
    cta: 'Free Removal', to: '/car-removal',
    cta2: 'Get Cash Offer', to2: '/estimate',
  },
];

const SERVICES = [
  { Icon: Zap,       title: 'Emergency Towing',           desc: '24/7 emergency towing across all Brisbane suburbs. Fast 30-min response guarantee.', features: ['24/7 available','30 min response','All suburbs'], featured: true, to: '/estimate?service=Emergency+Towing' },
  { Icon: Truck,     title: 'Standard Towing',            desc: 'Reliable scheduled towing for all cars, SUVs, utes and vans. Fully insured.', features: ['Fixed rates','GPS tracked','Insured'], to: '/estimate?service=Standard+Towing' },
  { Icon: DollarSign,title: 'Cash for Cars',              desc: 'Instant cash offers for any vehicle, any condition, anywhere in Brisbane.', features: ['Same day payment','All makes/models','Best prices'], to: '/estimate?service=Cash+for+Cars' },
  { Icon: Car,       title: 'Free Car Removal',           desc: 'FREE pickup of your unwanted vehicle. No hidden fees. We come to you.', features: ['100% free','Same day','All conditions'], to: '/estimate?service=Free+Car+Removal' },
  { Icon: Wrench,    title: 'Heavy Machinery Transport',  desc: 'Equipment transport up to 5 tons. Fully insured, experienced riggers.', features: ['Up to 5 tons','Fully insured','SEQ wide'], to: '/estimate?service=Heavy+Machinery+Transport' },
  { Icon: Users,     title: 'B2B Fleet & Yard Towing',    desc: 'Dedicated towing services for auto yards, dealerships & businesses.', features: ['Volume rates','Weekly billing','Priority response'], to: '/estimate?service=Standard+Towing' },
];

const AREAS = [
  { dir: '↑ North', suburbs: ['Chermside', 'Aspley', 'Zillmere', 'Strathpine', 'North Lakes', 'Caboolture'] },
  { dir: '→ East',  suburbs: ['Wynnum', 'Manly', 'Cleveland', 'Capalaba', 'Redlands', 'Birkdale'] },
  { dir: '↓ South', suburbs: ['Logan', 'Beenleigh', 'Browns Plains', 'Springwood', 'Sunnybank', 'Rochedale'] },
  { dir: '← West',  suburbs: ['Ipswich', 'Forest Lake', 'Inala', 'Richlands', 'Springfield', 'Goodna'] },
];

const STATS = [
  { val: '24/7',   label: 'Always Available' },
  { val: '30min',  label: 'Response Time' },
  { val: '5,000+', label: 'Jobs Completed' },
  { val: '4.9★',   label: 'Customer Rating' },
];

export default function HomePage() {
  const { settings, telHref, waHref } = useSettings();
  const [slide, setSlide]       = useState(0);
  const [fade, setFade]         = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [gallery, setGallery]   = useState([]);
  const [blog, setBlog]         = useState([]);

  const go = (i) => {
    setFade(false);
    setTimeout(() => { setSlide(((i % SLIDES.length) + SLIDES.length) % SLIDES.length); setFade(true); }, 280);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    api.get('/api/gallery?published_only=true').then(r => setGallery(r.data.slice(0, 6))).catch(() => {});
    api.get('/api/blog?published_only=true').then(r => setBlog(r.data.slice(0, 3))).catch(() => {});
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const s = SLIDES[slide];

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* ── Top bar ── */}
      <div style={{ backgroundColor: BL }} className="text-white text-xs py-2 px-4 hidden lg:flex">
        <div className="max-w-7xl mx-auto flex justify-between items-center w-full">
          <div className="flex gap-6 items-center">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" style={{ color: YW }} /> 24/7 Emergency Service</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" style={{ color: YW }} /> Brisbane &amp; All SE Queensland</span>
            <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5" style={{ color: YW }} /> 4.9★ — 5,000+ Satisfied Customers</span>
          </div>
          <div className="flex gap-6 items-center">
            {settings.company_email && (
              <a href={`mailto:${settings.company_email}`} className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors">
                <Mail className="w-3 h-3" /> {settings.company_email}
              </a>
            )}
            <a href={telHref()} className="flex items-center gap-1.5 font-bold hover:opacity-90" style={{ color: YW }}>
              <Phone className="w-3.5 h-3.5" /> {settings.company_phone}
            </a>
          </div>
        </div>
      </div>

      {/* ── Navbar ── */}
      <nav className={`sticky top-0 z-50 bg-white transition-all ${scrolled ? 'shadow-xl' : 'border-b border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={settings.company_name} className="h-12 object-contain" onError={e => e.target.style.display='none'} />
            ) : (
              <div style={{ backgroundColor: BL }} className="w-12 h-12 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6" style={{ color: YW }} />
              </div>
            )}
            <div>
              <div className="font-black text-xl leading-none" style={{ color: BL }}>
                {settings.company_name || 'APEX TOWING'}
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest uppercase">Solutions &amp; Equipment Transport</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8">
            {[['Services', '#services'], ['Areas', '#areas'], ['Gallery', '/gallery'], ['Blog', '/blog']].map(([l, to]) =>
              to.startsWith('#')
                ? <a key={l} href={to} className="text-gray-600 font-semibold hover:text-apex-blue-700 transition-colors text-sm">{l}</a>
                : <Link key={l} to={to} className="text-gray-600 font-semibold hover:text-apex-blue-700 transition-colors text-sm">{l}</Link>
            )}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/estimate" className="px-4 py-2.5 border-2 font-bold text-sm rounded-xl hover:bg-gray-50 transition-colors" style={{ borderColor: BL, color: BL }}>
              Free Estimate
            </Link>
            <a href={telHref()} className="flex items-center gap-2 text-white font-black px-5 py-2.5 rounded-xl text-sm hover:opacity-90 transition-all shadow-lg"
              style={{ backgroundColor: YW, color: BL }}>
              <Phone className="w-4 h-4" /> {settings.company_phone}
            </a>
          </div>

          <button onClick={() => setMenuOpen(v => !v)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            {menuOpen ? <X className="w-6 h-6" style={{ color: BL }} /> : <Menu className="w-6 h-6" style={{ color: BL }} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-2 shadow-lg">
            {[['🏠 Home','/'],[' Services','#services'],['📍 Areas Covered','#areas'],['🖼️ Gallery','/gallery'],['📝 Blog','/blog'],['💰 Get Estimate','/estimate'],['🚗 Free Car Removal','/car-removal'],['📅 Book Now','/book']].map(([l,to]) =>
              to.startsWith('#') || to === '/'
                ? <a key={l} href={to} onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50">{l}</a>
                : <Link key={l} to={to} onClick={() => setMenuOpen(false)} className="block py-2.5 px-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50">{l}</Link>
            )}
            <a href={telHref()} className="block text-center font-black py-3 rounded-xl text-apex-blue-700" style={{ backgroundColor: YW }}>
              📞 Call {settings.company_phone}
            </a>
          </div>
        )}
      </nav>

      {/* ── Hero Slideshow ── */}
      <section className="relative h-[580px] lg:h-[680px] overflow-hidden" style={{ backgroundColor: BL }}>
        <div className={`absolute inset-0 transition-opacity duration-500 ${fade ? 'opacity-100' : 'opacity-0'}`}>
          <img src={s.img} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(13,43,107,0.88) 0%, rgba(13,43,107,0.50) 60%, rgba(13,43,107,0.15) 100%)' }} />
        </div>

        {/* Hero content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 w-full grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              {/* Emergency badge */}
              <div className="inline-flex items-center gap-2 bg-red-600 text-white text-xs font-black px-3 py-1.5 rounded-full mb-5 animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full"></span>
                LIVE — Available Now
              </div>
              <h1 className={`text-4xl lg:text-5xl font-black text-white mb-4 leading-tight transition-all duration-500 ${fade ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                {s.headline}
              </h1>
              <p className={`text-white/75 text-lg mb-8 max-w-xl leading-relaxed transition-all duration-500 delay-75 ${fade ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                {s.sub}
              </p>
              <div className="flex flex-wrap gap-3">
                {s.to ? (
                  <Link to={s.to} className="flex items-center gap-2 px-7 py-4 rounded-xl font-black text-apex-blue-700 text-base shadow-xl hover:opacity-90 transition-all" style={{ backgroundColor: YW }}>
                    <Truck className="w-5 h-5" /> {s.cta}
                  </Link>
                ) : (
                  <a href={telHref()} className="flex items-center gap-2 px-7 py-4 rounded-xl font-black text-apex-blue-700 text-base shadow-xl hover:opacity-90 transition-all" style={{ backgroundColor: YW }}>
                    <Phone className="w-5 h-5" /> {settings.company_phone}
                  </a>
                )}
                <Link to={s.to2} className="flex items-center gap-2 px-7 py-4 rounded-xl font-black text-white text-base border-2 border-white/40 hover:bg-white/10 transition-all">
                  {s.cta2} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Quick links panel */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-5 space-y-2">
                <p className="text-white font-black text-sm uppercase tracking-wider mb-3">Quick Actions</p>
                {[
                  { icon: Calculator, label:'Get Free Estimate', sub:'Instant price guide', to:'/estimate', bg:'bg-blue-600' },
                  { icon: Phone,      label:'Call Us Now',        sub:'24/7 emergency line', to:null, bg:'bg-red-600' },
                  { icon: Calendar,   label:'Book a Tow',         sub:'Schedule online now', to:'/book', bg:'bg-purple-600' },
                  { icon: Car,        label:'Free Car Removal',   sub:'We come to you FREE', to:'/car-removal', bg:'bg-green-600' },
                  { icon: MessageCircle, label:'WhatsApp Us',     sub:'Quick quotes', to:null, wa:true, bg:'bg-green-500' },
                ].map(({ icon: Icon, label, sub, to, bg, wa }) => {
                  const inner = (
                    <div className="flex items-center gap-3">
                      <div className={`${bg} w-9 h-9 rounded-lg flex items-center justify-center shrink-0`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm leading-none">{label}</p>
                        <p className="text-white/50 text-xs mt-0.5">{sub}</p>
                      </div>
                    </div>
                  );
                  const cls = "flex items-center justify-between p-3 rounded-xl hover:bg-white/10 transition-colors cursor-pointer w-full text-left";
                  if (wa) return <a key={label} href={waHref()} target="_blank" rel="noreferrer" className={cls}>{inner}<ArrowRight className="w-4 h-4 text-white/40" /></a>;
                  if (!to) return <a key={label} href={telHref()} className={cls}>{inner}<ArrowRight className="w-4 h-4 text-white/40" /></a>;
                  return <Link key={label} to={to} className={cls}>{inner}<ArrowRight className="w-4 h-4 text-white/40" /></Link>;
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <button onClick={() => go(slide - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/25 text-white p-3 rounded-xl backdrop-blur-sm transition-all z-20">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => go(slide + 1)} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/25 text-white p-3 rounded-xl backdrop-blur-sm transition-all z-20">
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => go(i)} className={`rounded-full transition-all ${i === slide ? 'w-8 h-2 bg-apex-yellow-500' : 'w-2 h-2 bg-white/40'}`} />
          ))}
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <div style={{ backgroundColor: BL }} className="py-6">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map(({ val, label }) => (
            <div key={label} className="text-center">
              <p className="text-3xl font-black" style={{ color: YW }}>{val}</p>
              <p className="text-white/60 text-sm mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Services ── */}
      <section id="services" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: YW }}>What We Do</p>
            <h2 className="text-3xl lg:text-4xl font-black" style={{ color: BL }}>Our Services</h2>
            <p className="text-gray-500 mt-2 max-w-xl mx-auto">Professional towing and vehicle services across all Brisbane suburbs and SE Queensland</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map(({ Icon, title, desc, features, featured, to }) => (
              <Link key={title} to={to || '/book'} className={`group rounded-2xl p-6 border-2 flex flex-col hover:shadow-lg transition-all ${
                featured
                  ? 'border-apex-blue-700 bg-apex-blue-700 text-white'
                  : 'border-gray-200 bg-white hover:border-apex-blue-300'
              }`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${featured ? 'bg-white/15' : 'bg-apex-blue-50'}`}>
                  <Icon className={`w-6 h-6 ${featured ? 'text-white' : 'text-apex-blue-700'}`} />
                </div>
                {featured && <span className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: YW }}>Most Requested</span>}
                <h3 className={`font-black text-lg mb-2 ${featured ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                <p className={`text-sm mb-4 flex-1 ${featured ? 'text-white/70' : 'text-gray-500'}`}>{desc}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {features.map(f => (
                    <span key={f} className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      featured ? 'bg-white/15 text-white' : 'bg-apex-blue-50 text-apex-blue-700'
                    }`}>{f}</span>
                  ))}
                </div>
                <div className={`flex items-center gap-1 text-sm font-bold ${featured ? 'text-apex-yellow-400' : 'text-apex-blue-700'}`}>
                  Get Started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Areas We Serve ── */}
      <section id="areas" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: YW }}>Coverage</p>
            <h2 className="text-3xl lg:text-4xl font-black" style={{ color: BL }}>We Cover All of Brisbane</h2>
            <p className="text-gray-500 mt-2">North, South, East, West — if you're in Brisbane or SE Queensland, we're on our way</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {AREAS.map(({ dir, suburbs }) => (
              <div key={dir} className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                <p className="font-black text-apex-blue-700 mb-3">{dir}</p>
                <ul className="space-y-1.5">
                  {suburbs.map(sub => (
                    <li key={sub} className="text-sm text-gray-600 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-apex-yellow-500 shrink-0"></span>{sub}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-apex-blue-700 rounded-2xl p-6 text-center text-white">
            <p className="font-black text-xl mb-1">Not sure if we cover your area?</p>
            <p className="text-white/60 text-sm mb-4">We service Greater Brisbane, Gold Coast, Ipswich, Sunshine Coast and beyond — just call us.</p>
            <a href={telHref()} className="inline-flex items-center gap-2 font-black px-8 py-3 rounded-xl text-apex-blue-700 hover:opacity-90 transition-all" style={{ backgroundColor: YW }}>
              <Phone className="w-4 h-4" /> Call {settings.company_phone}
            </a>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: YW }}>Why Apex</p>
            <h2 className="text-3xl font-black" style={{ color: BL }}>Why Brisbane Chooses Us</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Zap, title: '30-Min Response', desc: 'We guarantee a 30-minute response for emergency towing anywhere in Brisbane metro.' },
              { icon: Shield, title: 'Fully Insured', desc: 'All vehicles handled with full insurance coverage. Your car is safe with us.' },
              { icon: DollarSign, title: 'Upfront Pricing', desc: 'No hidden charges. We quote you before we move — what we say is what you pay.' },
              { icon: Star, title: '4.9 Star Rating', desc: '5,000+ completed jobs with an outstanding reputation across Brisbane suburbs.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-xl bg-apex-blue-50 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-apex-blue-700" />
                </div>
                <h3 className="font-black text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gallery preview ── */}
      {gallery.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-end mb-8">
              <div>
                <p className="text-xs font-black uppercase tracking-widest mb-1" style={{ color: YW }}>Our Work</p>
                <h2 className="text-3xl font-black" style={{ color: BL }}>See Us in Action</h2>
              </div>
              <Link to="/gallery" className="flex items-center gap-1 font-bold text-apex-blue-700 hover:underline text-sm">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map(item => (
                <Link to="/gallery" key={item.id} className="relative aspect-video rounded-2xl overflow-hidden bg-gray-100 group">
                  <img src={item.url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => e.target.style.display='none'} />
                  <div className="absolute inset-0 bg-apex-blue-700/0 group-hover:bg-apex-blue-700/40 transition-colors flex items-center justify-center">
                    <span className="text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity text-sm">View Gallery</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Blog preview ── */}
      {blog.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-end mb-8">
              <div>
                <p className="text-xs font-black uppercase tracking-widest mb-1" style={{ color: YW }}>Tips & Advice</p>
                <h2 className="text-3xl font-black" style={{ color: BL }}>From Our Team</h2>
              </div>
              <Link to="/blog" className="flex items-center gap-1 font-bold text-apex-blue-700 hover:underline text-sm">
                All Posts <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {blog.map(p => (
                <Link to="/blog" key={p.id} className="bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all overflow-hidden">
                  {p.image && <img src={p.image} alt={p.title} className="w-full h-40 object-cover" onError={e => e.target.style.display='none'} />}
                  <div className="p-5">
                    <span className="text-[10px] font-black text-apex-blue-700 uppercase tracking-wider">{p.category}</span>
                    <h3 className="font-black text-gray-900 mt-1 mb-2 leading-snug">{p.title}</h3>
                    <p className="text-sm text-gray-400 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA Banner ── */}
      <section className="py-14" style={{ backgroundColor: YW }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-black mb-3" style={{ color: BL }}>Need a Tow Right Now?</h2>
          <p className="text-apex-blue-700/70 text-lg mb-7">We're available 24/7 across all Brisbane suburbs. Call or book online in under 2 minutes.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={telHref()} className="flex items-center gap-2 bg-apex-blue-700 text-white font-black px-8 py-4 rounded-xl text-lg hover:bg-apex-blue-600 shadow-xl">
              <Phone className="w-5 h-5" /> {settings.company_phone}
            </a>
            <Link to="/book" className="flex items-center gap-2 bg-white text-apex-blue-700 border-2 border-apex-blue-700 font-black px-8 py-4 rounded-xl text-lg hover:bg-apex-blue-50 shadow-xl">
              <Calendar className="w-5 h-5" /> Book Online
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ backgroundColor: BL }} className="text-white py-12">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-4 gap-8 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              {settings.logo_url
                ? <img src={settings.logo_url} alt={settings.company_name} className="h-10 object-contain" onError={e => e.target.style.display='none'} />
                : <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Truck className="w-5 h-5" style={{ color: YW }} /></div>
              }
              <div>
                <p className="font-black text-base leading-none">{settings.company_name}</p>
                <p className="text-white/40 text-[10px] tracking-widest uppercase">& Equipment Transport</p>
              </div>
            </div>
            <p className="text-white/50 text-xs leading-relaxed mb-4">Trusted towing across Brisbane and all of SE Queensland. Professional, insured, and available 24/7.</p>
            <div className="flex gap-3">
              {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Facebook className="w-4 h-4" /></a>}
              {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Instagram className="w-4 h-4" /></a>}
              {settings.tiktok_url && <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors text-xs font-black">TT</a>}
              {settings.whatsapp && <a href={waHref()} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><MessageCircle className="w-4 h-4" /></a>}
            </div>
          </div>

          <div>
            <p className="font-black text-sm uppercase tracking-wider mb-4" style={{ color: YW }}>Services</p>
            <ul className="space-y-2">
              {['Emergency Towing','Standard Towing','Cash for Cars','Free Car Removal','Heavy Transport','B2B Fleet'].map(s => (
                <li key={s}><Link to="/book" className="text-white/50 hover:text-white text-sm transition-colors">{s}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-black text-sm uppercase tracking-wider mb-4" style={{ color: YW }}>Areas</p>
            <ul className="space-y-2">
              {['North Brisbane','South Brisbane','East Brisbane','West Brisbane','Ipswich','Gold Coast'].map(a => (
                <li key={a} className="text-white/50 text-sm">{a}</li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-black text-sm uppercase tracking-wider mb-4" style={{ color: YW }}>Contact</p>
            <ul className="space-y-3">
              <li><a href={telHref()} className="flex items-center gap-2 text-sm hover:text-white transition-colors text-white/70"><Phone className="w-4 h-4 shrink-0" style={{ color: YW }} />{settings.company_phone}</a></li>
              {settings.company_email && <li><a href={`mailto:${settings.company_email}`} className="flex items-center gap-2 text-sm hover:text-white transition-colors text-white/70"><Mail className="w-4 h-4 shrink-0" style={{ color: YW }} />{settings.company_email}</a></li>}
              <li><span className="flex items-center gap-2 text-sm text-white/70"><MapPin className="w-4 h-4 shrink-0" style={{ color: YW }} />{settings.company_address}</span></li>
              <li><span className="flex items-center gap-2 text-sm text-white/70"><Clock className="w-4 h-4 shrink-0" style={{ color: YW }} />24/7 Emergency Service</span></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} {settings.company_name}. All rights reserved. Brisbane, QLD, Australia.</p>
          {settings.company_abn && <p className="text-white/30 text-xs">ABN: {settings.company_abn}</p>}
          <div className="flex gap-5">
            {[['Privacy Policy','#'],['Terms of Service','#']].map(([l,h]) => (
              <a key={l} href={h} className="text-white/30 hover:text-white/60 text-xs transition-colors">{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}